import { useState } from "react";
import ReactPlayer from "react-player";

import Skeleton from "@/components/Skeleton";
import useVideo from "@/lib/storyblok/Media/useVideo";
import type { StoryBlokMedia } from "@/types/storyblok";

// Get the stream url
const getStreamVideoUrl = (url: string) => {
  return `${
    process.env.NEXT_PUBLIC_STREAM_URL
  }/video/v1?url=${encodeURIComponent(url)}`;
};

import UI from "./UI";
import styles from "./video.module.scss";
import VideoControls from "./VideoControls";

interface VideoProps {
  media: StoryBlokMedia;
  showControls?: boolean;
}

const VimeoElement = ({ media, showControls }: VideoProps) => {
  const video = useVideo(media);
  const { player } = video;
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);

  const toggleMute = async () => {
    if (!player) return;
    const next = !muted;
    try {
      await player.setMuted(next);
      // Background mode starts at volume 0, so raise it when unmuting
      if (!next) await player.setVolume(1);
      setMuted(next);
    } catch {
      // Leave the button state as is if the player refuses
    }
  };

  const togglePause = async () => {
    if (!player) return;
    try {
      if (paused) {
        await player.play();
      } else {
        await player.pause();
      }
      setPaused(!paused);
    } catch {
      // Leave the button state as is if the player refuses
    }
  };

  return (
    <>
      <UI video={video} />
      {showControls && (
        <VideoControls
          muted={muted}
          paused={paused}
          onToggleMute={toggleMute}
          onTogglePause={togglePause}
        />
      )}
    </>
  );
};

const isVimeo = (file: string) => /vimeo\.com/i.test(file);

const FileVideo = ({ media, showControls }: VideoProps) => {
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);

  return (
    <>
      <div className={styles.videoWrap}>
        <ReactPlayer
          url={getStreamVideoUrl(media.file)}
          playsinline
          autoPlay={true}
          controls={false}
          muted={muted}
          playing={!paused}
          className="react-player"
          loop={true}
          fallback={<Skeleton height="100%" />}
        />
      </div>
      {showControls && (
        <VideoControls
          muted={muted}
          paused={paused}
          onToggleMute={() => setMuted((m) => !m)}
          onTogglePause={() => setPaused((p) => !p)}
        />
      )}
    </>
  );
};

const Video = ({ media, showControls }: VideoProps) => {
  if (media?.file && !isVimeo(media.file)) {
    return <FileVideo media={media} showControls={showControls} />;
  }
  return <VimeoElement media={media} showControls={showControls} />;
};

export default Video;
